import { useEffect, useState } from "react";

import { Box, Button, Divider, FormControl, FormErrorMessage, FormHelperText, FormLabel, Heading, HStack, Icon, Input, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { Field, FieldProps, Form, Formik } from "formik";
import { HttpStatusCode } from "axios";
import { FaEye } from "react-icons/fa";

import EditIcon from "@/assets/EditIcon";
import ArrowIcon from "@/assets/ArrowIcon";
import { COLORS, DURATION_TOAST } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import CurrencyInput from "@/components/Fields/CurrencyInput";
import Loader from "@/components/Layouts/Loader";
import FileUploader from "@/components/Fields/FileUploader";
import { settingSchema } from "@/schemas/settingSchema";
import { getSettingsById, putSetting } from "@/services/settingService";
import { downloadFileZip } from "@/services/downloadService";

import '../styles/Form.css'
import EditFile from "@/components/Fields/EditFile";

type FormContents = {
    clientId: string,
    ppn: string | number,
    adminEmailRecipient: string,
    emailCC: string,
    emailProcessCC: string,
    emailSireReview: string,
    shipAge: string | number,
    dwt: string | number,
    modifiedBy: string,
    files: File[],
    manualAdmin: File | null,
    manualFinance: File | null,
    manualShipOperator: File | null,
    manualInspectingCo: File | null,
    manualRequestor: File | null,
}

const General = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [dataDetail, setDataDetail] = useState<any>(null);
    const [dataEdit, setDataEdit] = useState<any>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [editableDocs, setEditableDocs] = useState<string[]>([]);
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [shipAge, setShipAge] = useState<number>(0);
    const [dwt, setDwt] = useState<number>(0);
    const [selectedManualAdmin, setSelectedManualAdmin] = useState<File | null>(null);
    const [selectedManualFinance, setSelectedManualFinance] = useState<File | null>(null);
    const [selectedManualShipOperator, setSelectedManualShipOperator] = useState<File | null>(null);
    const [selectedManualInspectingCo, setSelectedManualInspectingCo] = useState<File | null>(null);
    const [selectedManualRequestor, setselectedManualRequestor] = useState<File | null>(null);

    const showToast = (title: string, description: string, status: "success" | "error") => {
        toast({
            title,
            description,
            status,
            duration: DURATION_TOAST,
            isClosable: true,
        });
    };

    const handleSubmit = async (data: any) => {
        setTitle("Save Settings?");
        setConfirmationTest("Are you sure you want to edit?");
        setConfirmationTitleButton("Save");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const generateDataEdit = () => {
        const data = new FormData();
        data.append('clientId', dataEdit.clientId || '');
        data.append('ppn', dataEdit.ppn || '');
        data.append('adminEmailRecipient', dataEdit.adminEmailRecipient || '');
        data.append('emailCC', dataEdit.emailCC || '');
        data.append('emailProcessCC', dataEdit.emailProcessCC || '');
        data.append('emailSireReview', dataEdit.emailSireReview || '');
        data.append('InvoiceFormat', 'test');
        data.append('modifiedBy', dataEdit.modifiedBy || '');
        if (uploadedFiles?.length > 0) {
            for (let i = 0; i < uploadedFiles?.length; i++) {
                data.append('files', uploadedFiles[i]);
            }
        } else {
            data.append('files', '');
        }
        data.append('vesselYearsBuild', String(shipAge) || '');
        data.append('vesselDWT', String(dwt) || '');
        data.append('UserManualAdmin', selectedManualAdmin || '');
        data.append('UserManualAdminFinance', selectedManualFinance || '');
        data.append('UserManualShipOperator', selectedManualShipOperator || '');
        data.append('UserManualInspectingCompany', selectedManualInspectingCo || '');
        data.append('UserManualRequestor', selectedManualRequestor || '');
        // for (let [key, value] of data.entries()) {
        //     console.log(`${key}: ${value}`);
        // }
        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel to Edit Settings?") {
            onClose();
            return;
        }

        const obj = generateDataEdit();
        const response = await putSetting(obj);
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            showToast('Settings updated.', "We've updated settings for you.", 'success');
            // setTimeout(() => navigate('/settings/general'), 1000);
            getDataDetail();
        } else {
            showToast('Settings failed to update.', "We were unable to update settings for you.", 'error');
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleDownloadZip = async (idFile: string, uploadType: number) => {
        await downloadFileZip(idFile, uploadType);
    }

    const handleShowEditImage = (name: string) => {
        setEditableDocs((prevDocs) => {
            if (prevDocs.includes(name)) {
                return prevDocs.filter((doc) => doc !== name);
            }

            return [...prevDocs, name];
        });
    }

    const handleDrop = (acceptedFiles: File[], fieldName: string, setFieldValue: any) => {
        setUploadedFiles(acceptedFiles);
        setFieldValue(fieldName, acceptedFiles);
    };

    const getDataDetail = async () => {
        setIsLoading(true);
        const response = await getSettingsById('react-client-inspection');
        setIsLoading(false);
        if (response) {
            setDataDetail(response.data);
            setDwt(response?.data?.vesselDWT ? Number(response.data.vesselDWT) : 0);
            setShipAge(response?.data?.vesselYearsBuild ? Number(response.data.vesselYearsBuild) : 0);
        }
    }

    useEffect(() => {
        getDataDetail();
    }, [])

    const initialValues: FormContents = {
        clientId: dataDetail?.clientId || '',
        ppn: dataDetail?.ppn || '',
        adminEmailRecipient: dataDetail?.adminEmailRecipient || '',
        emailCC: dataDetail?.emailCC || '',
        emailProcessCC: dataDetail?.emailProcessCC || '',
        emailSireReview: dataDetail?.emailSireReview || '',
        shipAge: dataDetail?.vesselYearsBuild || '',
        dwt: dataDetail?.vesselDWT || '',
        modifiedBy: dataDetail?.modifiedBy || '',
        files: dataDetail?.supportingFileId || [],
        manualAdmin: dataDetail?.userManualAdminFileId || null,
        manualFinance: dataDetail?.userManualAdminFinanceFileId || null,
        manualShipOperator: dataDetail?.userManualShipOperatorFileId || null,
        manualInspectingCo: dataDetail?.userManualInspectingCompanyFileId || null,
        manualRequestor: dataDetail?.userManualRequestorFileId || null,
    }

    return (
        <>
            {isLoading ? <Loader /> :
                <>
                    <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
                    <div className="form-container">
                        <Formik
                            initialValues={initialValues}
                            validationSchema={settingSchema}
                            onSubmit={(values) => {
                                handleSubmit(values);
                            }}
                            enableReinitialize={true}
                        >
                            {(props) => (
                                <Form>
                                    <Heading size="md" mb={5} mt={5}>Email Recipients</Heading>
                                    <Divider mb={5} />
                                    <Field name='adminEmailRecipient'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.adminEmailRecipient && form.touched.adminEmailRecipient)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Admin Email Recipient</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Admin Email Recipient' className="form-input" />
                                                        <FormErrorMessage>{String(form.errors.adminEmailRecipient)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='emailCC'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.emailCC && form.touched.emailCC)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Email CC Invoice</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Email CC for Invoices' className="form-input" />
                                                        <FormErrorMessage>{String(form.errors.emailCC)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='emailProcessCC'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.emailProcessCC && form.touched.emailProcessCC)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <div className="form-label">
                                                        <FormLabel>Email CC Process</FormLabel>
                                                        <FormHelperText>(Nomination, SIRE Review)</FormHelperText>
                                                    </div>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Email CC for Processes' className="form-input" id="form-email-process" />
                                                        <FormErrorMessage>{String(form.errors.emailProcessCC)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='emailSireReview'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.emailSireReview && form.touched.emailSireReview)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Email Admin SIRE Review</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Email CC for SIRE Reviews' className="form-input" />
                                                        <FormErrorMessage>{String(form.errors.emailSireReview)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />
                                    <Heading size="md" mb={5} mt={5}>Settings</Heading>
                                    <Divider mb={5} />
                                    <Field name='ppn'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.ppn && form.touched.ppn)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">PPN</FormLabel>
                                                    <div className="form-input-container">
                                                        <div className="form-input">
                                                            <CurrencyInput
                                                                {...field}
                                                                placeholder='Input PPN'
                                                                prefix=''
                                                                value={props.values.ppn}
                                                                error={Boolean(form.errors.ppn && form.touched.ppn)}
                                                                onValueChange={({ floatValue = 0 }) => props.setFieldValue('ppn', floatValue)}
                                                                disabled={isSubmitting}
                                                                width={'7em'}
                                                            />
                                                            <span style={{ marginLeft: 5 }}>%</span>
                                                        </div>
                                                        <FormErrorMessage>{String(form.errors.ppn)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='shipAge'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.shipAge && form.touched.shipAge)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Ship Age</FormLabel>
                                                    <div className="form-input-container">
                                                        <div className="form-input">
                                                            <CurrencyInput
                                                                {...field}
                                                                placeholder='Ship Age'
                                                                prefix=''
                                                                value={props.values.shipAge}
                                                                error={Boolean(form.errors.shipAge && form.touched.shipAge)}
                                                                onValueChange={({ floatValue = 0 }) => {
                                                                    setShipAge(floatValue);
                                                                    props.setFieldValue('shipAge', floatValue);
                                                                }}
                                                                disabled={isSubmitting}
                                                                width={'7em'}
                                                                decimalScale={0}
                                                            />
                                                            <span style={{ marginLeft: 5 }}>Years</span>
                                                        </div>
                                                        <FormErrorMessage>{String(form.errors.shipAge)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                                <FormHelperText>(Vessels older than this many years will have CAP/CAS survey mandatory for SIRE review)</FormHelperText>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='dwt'>
                                        {({ field, form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.dwt && form.touched.dwt)} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">DWT</FormLabel>
                                                    <div className="form-input-container">
                                                        <div className="form-input">
                                                            <CurrencyInput
                                                                {...field}
                                                                placeholder='DWT'
                                                                prefix=''
                                                                value={props.values.dwt}
                                                                error={Boolean(form.errors.dwt && form.touched.dwt)}
                                                                onValueChange={({ floatValue = 0 }) => {
                                                                    setDwt(floatValue);
                                                                    props.setFieldValue('dwt', floatValue);
                                                                }}
                                                                disabled={isSubmitting}
                                                                width={'12em'}
                                                                decimalScale={0}
                                                            />
                                                            <span style={{ marginLeft: 5 }}>Tonnes</span>
                                                        </div>
                                                        <FormErrorMessage>{String(form.errors.dwt)}</FormErrorMessage>
                                                    </div>
                                                </div>
                                                <FormHelperText>(Vessels with DWT higher than this will have CAP/CAS survey mandatory for SIRE review)</FormHelperText>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />
                                    <Heading size="md" mb={5} mt={5}>Files</Heading>
                                    <Divider mb={5} />
                                    <Field name='files'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.files && form.submitCount)} style={{ marginBottom: 15 }}>
                                                <div>
                                                    <div className="form-file-field-container">
                                                        <FormLabel className="form-label">Supporting Files</FormLabel>
                                                        <div className="form-input-file">
                                                            {dataDetail?.supportingFileId && <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                                <Box
                                                                    borderWidth="1px"
                                                                    borderRadius="md"
                                                                    padding={4}
                                                                    backgroundColor={dataDetail?.supportingFileId ? "green.50" : "yellow.50"}
                                                                    borderColor={dataDetail?.supportingFileId ? "green.200" : "yellow.200"}
                                                                    width="100%"
                                                                    marginBottom="15px"
                                                                >
                                                                    <HStack
                                                                        borderWidth="1px"
                                                                        borderRadius="md"
                                                                        padding={3}
                                                                        backgroundColor="white"
                                                                        justifyContent="space-between"
                                                                        width="100%"
                                                                    >
                                                                        <HStack>
                                                                            <Text>{dataDetail?.supportingFileId ? 'Supporting Files' : 'No file uploaded'}</Text>
                                                                        </HStack>
                                                                        <HStack cursor='pointer' onClick={() => handleDownloadZip(dataDetail?.supportingFileId, 14)}>
                                                                            {dataDetail?.supportingFileId && <Icon as={FaEye} />}
                                                                            <Text>{dataDetail?.supportingFileId ? 'Download Document' : ''}</Text>
                                                                        </HStack>
                                                                    </HStack>
                                                                </Box>
                                                                <EditIcon
                                                                    style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                                    onClick={() => handleShowEditImage('files')}
                                                                />
                                                            </div>}

                                                            {(!dataDetail?.supportingFileId || editableDocs.includes('files')) &&
                                                                <div style={{ width: '95%' }}>
                                                                    <FileUploader
                                                                        onDrop={(e) => handleDrop(e, 'files', props.setFieldValue)}
                                                                        isInvalid={Boolean(form.errors.files && form.submitCount)}
                                                                        type="faktur"
                                                                        isDisabled={props.isSubmitting}
                                                                        isPadding={false}
                                                                    />
                                                                </div>
                                                            }
                                                            <FormErrorMessage>{String(form.errors.files)}</FormErrorMessage>
                                                        </div>
                                                    </div>
                                                    
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='manualAdmin'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.manualAdmin && form.submitCount)} style={{ marginBottom: 15 }}>
                                                <div className="form-file-field-container">
                                                    <FormLabel className="form-label">Super Admin User Manual</FormLabel>
                                                    <div className="form-input-file">
                                                        <EditFile
                                                            type="manual"
                                                            title="Super Admin User Manual"
                                                            fileId={dataDetail?.userManualAdminFileId}
                                                            handleDrop={(files) => {
                                                                if (files[0]) {
                                                                    setSelectedManualAdmin(files[0]);
                                                                    props.setFieldValue('manualAdmin', files[0]);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <FormErrorMessage>{String(form.errors.manualAdmin)}</FormErrorMessage>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='manualFinance'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.manualFinance && form.submitCount)} style={{ marginBottom: 15 }}>
                                                <div className="form-file-field-container">
                                                    <FormLabel className="form-label">Finance User Manual</FormLabel>
                                                    <div className="form-input-file">
                                                        <EditFile
                                                            type="manual"
                                                            title="Finance User Manual"
                                                            fileId={dataDetail?.userManualAdminFinanceFileId}
                                                            handleDrop={(files) => {
                                                                if (files[0]) {
                                                                    setSelectedManualFinance(files[0]);
                                                                    props.setFieldValue('manualFinance', files[0]);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <FormErrorMessage>{String(form.errors.manualFinance)}</FormErrorMessage>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='manualShipOperator'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.manualShipOperator && form.submitCount)} style={{ marginBottom: 15 }}>
                                                <div className="form-file-field-container">
                                                    <FormLabel className="form-label">Ship Operator User Manual</FormLabel>
                                                    <div className="form-input-file">
                                                        <EditFile
                                                            type="manual"
                                                            title="Ship Operator User Manual"
                                                            fileId={dataDetail?.userManualShipOperatorFileId}
                                                            handleDrop={(files) => {
                                                                if (files[0]) {
                                                                    setSelectedManualShipOperator(files[0]);
                                                                    props.setFieldValue('manualShipOperator', files[0]);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <FormErrorMessage>{String(form.errors.manualShipOperator)}</FormErrorMessage>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='manualInspectingCo'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.manualInspectingCo && form.submitCount)} style={{ marginBottom: 15 }}>
                                                <div className="form-file-field-container">
                                                    <FormLabel className="form-label">Inspecting Company User Manual</FormLabel>
                                                    <div className="form-input-file">
                                                        <EditFile
                                                            type="manual"
                                                            title="Inspecting Company User Manual"
                                                            fileId={dataDetail?.userManualInspectingCompanyFileId}
                                                            handleDrop={(files) => {
                                                                if (files[0]) {
                                                                    setSelectedManualInspectingCo(files[0]);
                                                                    props.setFieldValue('manualInspectingCo', files[0]);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <FormErrorMessage>{String(form.errors.manualShipOperator)}</FormErrorMessage>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='manualRequestor'>
                                        {({ form }: FieldProps) => (
                                            <FormControl isInvalid={Boolean(form.errors.manualRequestor && form.submitCount)} style={{ marginBottom: 15 }}>
                                                <div className="form-file-field-container">
                                                    <FormLabel className="form-label">SIRE Requestor User Manual</FormLabel>
                                                    <div className="form-input-file">
                                                        <EditFile
                                                            type="manual"
                                                            title="SIRE Requestor User Manual"
                                                            fileId={dataDetail?.userManualRequestorFileId}
                                                            handleDrop={(files) => {
                                                                if (files[0]) {
                                                                    setselectedManualRequestor(files[0]);
                                                                    props.setFieldValue('manualRequestor', files[0]);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    <FormErrorMessage>{String(form.errors.manualRequestor)}</FormErrorMessage>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            mt={4}
                                            bgColor={COLORS.BUTTON.BACKGROUND}
                                            color={COLORS.BUTTON.FOREGROUND}
                                            isLoading={isSubmitting}
                                            type='submit'
                                            rightIcon={<ArrowIcon />}
                                        >
                                            Save Settings
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </>}
        </>
    )
}

export default General;
